/* ============================================================
   Aklasu mega menu — sub-item image hover swap
   ------------------------------------------------------------
   Each sub-menu item carries `data-menu-bind="<handle>"`.
   Each .mega-menu-image carries `data-menu-bind` matching the
   item it represents. We mark exactly one image as `.is-active`
   at any time. CSS shows only that one (opacity 1).

   Two modes, controlled by the section setting `lock_parent_
   image` (rendered on the panel as `data-lock-parent-image`):

   1. SWAP MODE (default): the FIRST .mega-menu-image is active
      when the panel first opens. On sub-item mouseenter, swap
      to the matching image. On panel mouseleave, reset to first.

   2. PARENT-LOCK MODE: the image bound to the panel's parent
      handle (e.g. `shop`, `journal`) is set active and stays
      active. No hover listeners attached. Useful while the
      brand is still building per-sub-item images — bind one
      image to each top-level handle and flip the toggle off
      later to enable swap.
   ------------------------------------------------------------ */
(() => {
  const init = () => {
    document
      .querySelectorAll('.header__submenu.list-menu--disclosure')
      .forEach((panel) => {
        const images = panel.querySelectorAll('.mega-menu-image');
        if (images.length === 0) return;

        const isLocked = panel.dataset.lockParentImage === 'true';
        const parentHandle = panel.dataset.parentHandle;

        // Pick the default active image. In parent-lock mode,
        // prefer the image bound to the panel's parent handle.
        // In swap mode, use the first rendered image.
        let defaultImage = images[0];
        if (isLocked && parentHandle) {
          const parentImg = panel.querySelector(
            `.mega-menu-image[data-menu-bind="${parentHandle}"]`
          );
          if (parentImg) defaultImage = parentImg;
        }
        defaultImage.classList.add('is-active');

        // Parent-lock mode: stop here. No hover swap.
        if (isLocked) return;

        const setActive = (handle) => {
          let matched = false;
          images.forEach((img) => {
            const isMatch = img.dataset.menuBind === handle;
            img.classList.toggle('is-active', isMatch);
            if (isMatch) matched = true;
          });
          // Hovered sub-item has no bound image — show the default.
          if (!matched) images[0].classList.add('is-active');
        };

        panel.querySelectorAll('li[data-menu-bind]').forEach((li) => {
          li.addEventListener('mouseenter', () => {
            setActive(li.dataset.menuBind);
          });
        });

        // Cursor leaves the entire dropdown — reset to default.
        panel.addEventListener('mouseleave', () => {
          images.forEach((img, i) =>
            img.classList.toggle('is-active', i === 0)
          );
        });
      });
  };

  /* ============================================================
     Hide Shopify Inbox chat widget when overlays are open
     ------------------------------------------------------------
     The CSS `:has()` selector approach was unreliable here —
     Shopify Inbox sometimes renders late / inside a custom
     element whose state changes don't trigger `:has()`
     re-evaluation, depending on the browser. Watching the
     `[open]` attribute via MutationObserver and toggling a
     body class is the robust fallback. CSS hides the chat
     widget when `body.ak-overlay-open` is set.
     ------------------------------------------------------------ */
  const watchOverlays = () => {
    const triggers = document.querySelectorAll(
      '.menu-drawer-container, details-modal, details.mobile-facets__disclosure'
    );
    if (!triggers.length) return;

    const update = () => {
      let anyOpen = false;
      let searchOpen = false;
      let filterOpen = false;
      Array.from(triggers).forEach((t) => {
        const det = t.tagName === 'DETAILS' ? t : t.querySelector('details');
        if (det && det.hasAttribute('open')) {
          anyOpen = true;
          // Distinguish three overlay families. The body picks up
          // `.ak-overlay-open` for any of them (chat-bubble hide).
          // `.ak-search-open` is search-only (desktop logo/icon hide).
          // `.ak-filter-open` is filter-only (z-index lift on the
          // collection-filters parent stacking context — must NOT
          // fire for drawer/search or the FILTER button bleeds
          // through into the drawer's z-index zone).
          if (t.tagName === 'DETAILS-MODAL') searchOpen = true;
          if (t.classList && t.classList.contains('mobile-facets__disclosure')) filterOpen = true;
        }
      });
      document.body.classList.toggle('ak-overlay-open', anyOpen);
      document.body.classList.toggle('ak-search-open', searchOpen);
      document.body.classList.toggle('ak-filter-open', filterOpen);
    };

    const observer = new MutationObserver(update);
    triggers.forEach((t) => {
      const det = t.tagName === 'DETAILS' ? t : t.querySelector('details');
      if (det) {
        observer.observe(det, { attributes: true, attributeFilter: ['open'] });
      }
    });
    update();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      watchOverlays();
    });
  } else {
    init();
    watchOverlays();
  }
})();
